import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { InputGroup } from 'react-bootstrap';
import { Tooltip, IconButton } from '@sumologic/yama-ds-components';

import Icon from '~/components/Icon/Icon';
import Translate from '~/components/Translate/Translate';

import PreferencesContext from '~/containers/Preferences/PreferencesContext';

import Query from './Query/Query';
import InputBar from './InputBar/InputBar';

//----------------------------------------------------------------------------------------------------------------------

const RE_exp1 = RegExp('((:|>?=?)s*#{1})|(<?=?s*#{1})');
const RE_exp2 = /(:|>?=?)\s*#{1}|<?=?\s*#{1}/;
const RE_exp3 = RegExp('[^<>=!,:+]=');
const RE_exp4 = RegExp('!=|<>|><');
const RE_exp5 = RegExp('>=|:');

const RE_dYMWDH = /\d+[YMWDH]/g;

const RE_GE = RegExp('>=');
const RE_GT = RegExp('>');
const RE_LE = RegExp('<=');
const RE_LT = RegExp('<');

const RE_AND = / AND /g;
const RE_OR = / OR /g;

//----------------------------------------------------------------------------------------------------------------------

export const clientSideSections = [
    'creation-automation',
    'incident templates',
    'automation rules',
    'integrations',
    'playbook',
    'playbook executions',
    'objects',
    'bridge',
    'groups',
    'tasks',
    'notes'
];

//----------------------------------------------------------------------------------------------------------------------

const FilterBar = ({
    keyMap,
    completions,
    availableQueries,
    updateQueries,
    enableQueries,
    enableBookmarks,
    section, // todo: investigate why not always updated before subsection navigation, e.g.: #setting_user_interface
    sectionOptions,
    handleSection,
    getEditorApi
}) => {
    const preferencesContext = useContext(PreferencesContext);

    const [activateDateModals, setActivateDateModals] = useState(false);
    const [activateHelpModals, setActivateHelpModals] = useState(false);
    const editorViewRef = useRef(null);

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const isClientSide = clientSideSections.includes(section);

    let _keyMap = false;
    const _prepareKeyMap = () => {
        if (keyMap) {
            _keyMap = {};
            for (const key of Object.keys(keyMap)) {
                const keyCaseInsensitive = key.toLowerCase();
                if (keyCaseInsensitive !== key) {
                    _keyMap[keyCaseInsensitive] = keyMap[key];
                }
            }
        }
    };

    const _buildFilterFunction = value => {
        // console.log('_buildFilterFunction, (client) parse:', value);
        if (preferencesContext) {
            preferencesContext.updateFilterFunction?.(items => {
                /**
                 * Parse logical expression and filter values
                 *  @author mr
                 */
                const CROSS = '1';
                const VAL_CONT = '1';
                const CROSS_CONT = '11'; // CROSS + VAL_CONT
                const COL = '2';
                const COL_CONT = '21'; // COL + VAL_CONT
                const VAL_EXACT = '2';
                const CROSS_EXACT = '12'; // CROSS + VAL_EXACT
                const COL_EXACT = '22'; // COL + VAL_EXACT

                const EQ = '31';
                const NE = '32';
                const GT = '33';
                const GE = '34';
                const LT = '35';
                const LE = '36';

                const DATE = '0';
                const EQD = '310'; // EQ + DATE
                const GTD = '330'; // GT + DATE
                const GED = '340'; // GE + DATE
                const LTD = '350'; // LT + DATE
                const LED = '360'; // LE + DATE

                const TIME_INTERVAL = '1';
                const EQT = '311'; // EQ + TIME_INTERVAL
                const GTT = '331'; // GT + TIME_INTERVAL
                const GET = '341'; // GE + TIME_INTERVAL
                const LTT = '351'; // LT + TIME_INTERVAL
                const LET = '351'; // LE + TIME_INTERVAL

                const FE_NEXT = 1;
                const NE_FIRST = 2;
                const NE_NEXT = 3;
                const ODM = 86400; // 24 * 60 * 60; (24 hours * 60 minutes * 60 seconds)
                const OHM = 3600; // 60 * 60 (60 minutes * 60 seconds)

                const CN_HIDE = 'hide';
                const CN_OR = 'or';

                /*
                const LOG_LEV_ALL = 1;
                const LOG_LEV_ANY = 2;
                const LOG_LEV_FEW = 3;
                const LOG_SEP_SPACE = ' ';
                const LOG_SEP_NL = '\n';
                */

                function notEqual(value, e, i) {
                    // noinspection EqualityComparisonWithCoercionJS
                    return value != e[i];
                }

                function isExact(value) {
                    const len = value.length;
                    const x = value[0];
                    // prettier-ignore
                    return (
                        (
                            "'" === x ||
                            '"' === x
                        ) && (
                            (
                                2 < len &&
                                x === value[len - 1]
                            ) || (
                                2 === len &&
                                x === value[1]
                            )
                        )
                    );
                }

                function crossExact(value, e) {
                    let result = false;
                    for (const v of Object.values(e)) {
                        if (value === String(v)) {
                            result = true;
                            break;
                        }
                    }
                    return result;
                }

                function crossContain(value, e) {
                    let result = false;
                    const lc = value.toLowerCase();
                    for (const v of Object.values(e)) {
                        if (-1 !== String(v).toLowerCase().indexOf(lc)) {
                            result = true;
                            break;
                        }
                    }
                    return result;
                }

                function colContain(value, e, i) {
                    return (
                        -1 !==
                        String(e[i]).toLowerCase().indexOf(value.toLowerCase())
                    );
                }

                function colExact(value, e, i) {
                    const x = e[i];
                    // noinspection EqualityComparisonWithCoercionJS
                    return value == ('object' !== typeof x ? x : x?.sortValue);
                }

                function greaterThan(value, e, i) {
                    const x = e[i];
                    return value < ('object' !== typeof x ? x : x?.sortValue);
                }

                function greaterEq(value, e, i) {
                    const x = e[i];
                    return value <= ('object' !== typeof x ? x : x?.sortValue);
                }

                function lessThan(value, e, i) {
                    const x = e[i];
                    return value > ('object' !== typeof x ? x : x?.sortValue);
                }

                function lessEq(value, e, i) {
                    const x = e[i];
                    return value >= ('object' !== typeof x ? x : x?.sortValue);
                }

                /*
                let logLevel = 0;
                function writeLog(level, sep = LOG_SEP_SPACE, ...args) {
                    if (logLevel >= level) {
                        console.log(args.join(sep));
                    }
                }
                */

                let val = value.trim();
                const j = val.length - 1;
                while ('{' === val[0] && '}' === val[j]) {
                    // ++logLevel;
                    val = val.slice(1, j);
                }
                // writeLog(LOG_LEV_ALL, LOG_SEP_SPACE, 'Log Level:', logLevel);
                val = val
                    .replace(RE_AND, '\u0020,\u0020')
                    .replace(RE_OR, '\u0020+\u0020');
                // console.log(val);
                const manageValue = (items, value, step = 0) => {
                    let val = value;
                    let filterCols;
                    let index = -1;
                    let timeInterval = {
                        year: 0,
                        month: 0,
                        week: 0,
                        day: 0,
                        hour: 0
                    };
                    // noinspection JSUnusedAssignment
                    let mode = '';
                    if (RE_exp1.test(val)) {
                        let strSplit = val.match(RE_exp2);
                        if (RE_exp5.test(val)) {
                            mode = GE;
                        } else if (RE_GT.test(val)) {
                            mode = GT;
                        } else if (RE_LE.test(val)) {
                            mode = LE;
                        } else if (RE_LT.test(val)) {
                            mode = LT;
                        } else {
                            mode = EQ;
                        }
                        filterCols = val.split(strSplit[0]);
                        const chars = filterCols[1];
                        const firstChar = chars[0];
                        const lastChar = chars[chars.length - 1];
                        if (
                            ('[' === firstChar && ']' === lastChar) ||
                            ('|' === firstChar && '|' === lastChar)
                        ) {
                            let count = 0;
                            let match;
                            while ((match = RE_dYMWDH.exec(filterCols[1]))) {
                                count++;
                                const m = match[0];
                                const j = m.length - 1;
                                const n = parseInt(m.substring(0, j), 10);
                                switch (m.substring(j)) {
                                    case 'Y':
                                        timeInterval.year = n;
                                        break;
                                    case 'M':
                                        timeInterval.month = n;
                                        break;
                                    case 'W':
                                        timeInterval.week = n;
                                        break;
                                    case 'D':
                                        timeInterval.day = n;
                                        break;
                                    case 'H':
                                        timeInterval.hour = n;
                                        break;
                                }
                                if (count >= 5) {
                                    break;
                                }
                            }
                            mode += TIME_INTERVAL;
                        } else {
                            mode += DATE;
                        }
                    } else if (RE_exp3.test(val)) {
                        filterCols = val.split('=');
                        mode = EQ;
                    } else if (RE_exp4.test(val)) {
                        filterCols = val.split('!=');
                        if (1 === filterCols.length) {
                            filterCols = val.split('<>');
                            if (1 === filterCols.length) {
                                filterCols = val.split('><');
                            }
                        }
                        mode = NE;
                    } else if (RE_GT.test(val)) {
                        if (RE_GE.test(val)) {
                            filterCols = val.split('>=');
                            mode = GE;
                        } else {
                            filterCols = val.split('>');
                            mode = GT;
                        }
                    } else if (RE_LT.test(val)) {
                        if (RE_LE.test(val)) {
                            filterCols = val.split('<=');
                            mode = LE;
                        } else {
                            filterCols = val.split('<');
                            mode = LT;
                        }
                    } else {
                        filterCols = val.split(':');
                        if (1 === filterCols.length) {
                            mode = CROSS;
                            val = filterCols[0].trim();
                        } else {
                            mode = COL;
                            val = filterCols[1].trim();
                        }
                        if (isExact(val)) {
                            mode += VAL_EXACT;
                        } else {
                            mode += VAL_CONT;
                        }
                    }
                    // writeLog(LOG_LEV_ALL, LOG_SEP_SPACE, 'mode:', mode);
                    if (1 === filterCols.length) {
                        val = filterCols[0].trim();
                        // writeLog(LOG_LEV_ANY, LOG_SEP_SPACE, 'value:', val);
                    } else {
                        val = filterCols[1].trim();
                        // writeLog(LOG_LEV_ANY, LOG_SEP_SPACE, 'value:', val);
                        const { YMDHMS, regExpDate } = SETTINGS;
                        let result = val.match(regExpDate);
                        // writeLog(LOG_LEV_FEW, LOG_SEP_SPACE, 'BEGIN: subDatetoTS(val) |', val);
                        // writeLog(LOG_LEV_ALL, LOG_SEP_SPACE, "SETTINGS.dateFormats.longDateTime |", SETTINGS.dateFormats.longDateTime);
                        // writeLog(LOG_LEV_ALL, LOG_SEP_SPACE, 'SETTINGS.regExpDate |', regExpDate);
                        // writeLog(LOG_LEV_ANY, LOG_SEP_NL, 'YMDHMS', SETTINGS.YMDHMS, 'val:', val, 'match:', result);
                        if (val === result?.[0]) {
                            // writeLog(LOG_LEV_FEW, LOG_SEP_NL, 'getRegExpDate:',result.map((e,i)=> `${i}:e`),join());
                            const x = result[4] || 0;
                            const ts = Date.UTC(
                                result[YMDHMS[0]],
                                result[YMDHMS[1]] - 1,
                                result[YMDHMS[2]],
                                x && result[YMDHMS[3]],
                                x && result[YMDHMS[4]],
                                x && (0 !== YMDHMS[5] ? result[YMDHMS[5]] : 0)
                            );
                            // writeLog(LOG_LEV_ALL, LOG_SEP_SPACE, 'timestamp: ', ts, ' = ', new Date(ts).toISOString());
                            val = val.replace(regExpDate, ts / 1000);
                        }
                        // writeLog(LOG_LEV_FEW, LOG_SEP_SPACE, 'END: subDatetoTS(val) |', val);
                        // writeLog(LOG_LEV_ANY, LOG_SEP_SPACE, 'subDatetoTS(val):', val);
                    }
                    if (isExact(val)) {
                        val = val.substring(1, val.length - 1);
                        if ('' === val) {
                            val = undefined;
                            /*
                             because if a value is empty then the corresponding key is not there
                             (and therefore the value for the match must be undefined)
                            */
                        }
                    }
                    if (!_keyMap) {
                        //AB: regenerate _keyMap
                        _prepareKeyMap();
                    }
                    if (mode !== CROSS_CONT && mode !== CROSS_EXACT) {
                        // _keyMap : contains all the possible keys
                        // filterCols : contains the type to filter in 2 values: (type, value)
                        // noinspection JSUnusedAssignment
                        index = _keyMap[filterCols[0].trim().toLowerCase()];
                    }
                    const now = Date.now();
                    if (
                        mode === EQD ||
                        mode === GTD ||
                        mode === GED ||
                        mode === LTD ||
                        mode === LED
                    ) {
                        const str = new Date(now).toISOString();
                        val =
                            Date.parse(str.split('T')[0]) / 1000 -
                            filterCols[1] * ODM;
                    }
                    if (
                        mode === EQT ||
                        mode === GTT ||
                        mode === GET ||
                        mode === LTT ||
                        mode === LET
                    ) {
                        const n =
                            timeInterval.year * 365 +
                            timeInterval.month * 30 +
                            timeInterval.week * 7 +
                            timeInterval.day;
                        val = ODM * n + OHM * timeInterval.hour;
                        // writeLog(LOG_LEV_ANY, LOG_SEP_SPACE, 'timestamp #||:', val, '=', new Date(val * 1000).toISOString());
                        if ('[' === filterCols[1][0]) {
                            val =
                                Math.floor(now / 1000) +
                                CURRENT_USER.offset2num -
                                val;
                            // writeLog(LOG_LEV_ANY, LOG_SEP_SPACE, 'timestamp #[]:', val, '=', new Date(val * 1000).toISOString());
                        }
                    }
                    const filterItems = (logic, col = index) => {
                        let count = 0;
                        items.forEach(item => {
                            //TODO: check why sometime item is a string
                            if ('string' !== typeof item) {
                                const cls = item._className;
                                switch (step) {
                                    case FE_NEXT:
                                        if (cls === CN_HIDE) {
                                            return;
                                        }
                                        break;

                                    case NE_FIRST:
                                        if (cls !== CN_HIDE) {
                                            return;
                                        }
                                        break;

                                    case NE_NEXT:
                                        if (cls !== CN_OR) {
                                            return;
                                        }
                                        break;
                                }
                                // noinspection CommaExpressionJS
                                item._className = logic(val, item, col)
                                    ? (++count,
                                      // writeLog(LOG_LEV_FEW, LOG_SEP_SPACE, 'value:', val, 'item:', JSON.stringify(item)),
                                      step === NE_FIRST || step === NE_NEXT
                                          ? CN_OR
                                          : '')
                                    : CN_HIDE;
                            }
                        });
                        return count;
                    };
                    switch (mode) {
                        case CROSS_CONT:
                            return filterItems(crossContain, -1);
                        case CROSS_EXACT:
                            return filterItems(crossExact, -1);
                        case COL_CONT:
                            return filterItems(colContain);
                        case COL_EXACT:
                            return filterItems(colExact);
                        case EQ:
                        case EQD:
                        case EQT:
                            return filterItems(colExact);
                        case NE:
                            return filterItems(notEqual);
                        case GT:
                        case GTD:
                        case GTT:
                            return filterItems(greaterThan);
                        case GE:
                        case GED:
                        case GET:
                            return filterItems(greaterEq);
                        case LT:
                        case LTD:
                        case LTT:
                            return filterItems(lessThan);
                        case LE:
                        case LED:
                        case LET:
                            return filterItems(lessEq);
                    }
                };
                const filterOr = val.split('+');
                const filterAnd = filterOr[0].split(',');
                if (manageValue(items, filterAnd[0])) {
                    for (let i = 1; i < filterAnd.length; ++i) {
                        if (!manageValue(items, filterAnd[i], FE_NEXT)) {
                            break;
                        }
                    }
                }
                // Manage from second to next expressions
                for (let i = 1; i < filterOr.length; ++i) {
                    if (i > 1) {
                        // Clear class .or
                        items.forEach(item => {
                            if (CN_OR === item._className) {
                                item._className = '';
                            }
                        });
                    }
                    let filterAnd = filterOr[i].split(',');
                    if (manageValue(items, filterAnd[0], NE_FIRST)) {
                        for (let i = 1; i < filterAnd.length; ++i) {
                            if (!manageValue(items, filterAnd[i], NE_NEXT)) {
                                break;
                            }
                        }
                    }
                }
                const result = [];
                for (const item of items) {
                    if (CN_HIDE !== item._className) {
                        delete item._className;
                        result.push(item);
                    }
                }
                // console.log({ items, result });
                return result;
            });
            preferencesContext.setClientRowsAndApplyFilter();
        }
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const searchValue = value => {
        if (section) {
            // console.log('searchValue', { section, value });
            if (isClientSide) {
                _buildFilterFunction(value);
            }
            preferencesContext.setFilterBarString(value);
        }
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const renderInfoAndCalendar = () => {
        const InputbarButton = props => {
            const buttonClassname = `inputbar-btn ${props.className}`;
            return (
                <Tooltip
                    title={<Translate message={props.tip} />}
                    placement="bottom"
                    arrow
                >
                    <IconButton
                        className={buttonClassname}
                        disabled={props.disabled}
                        onClick={props.onClick}
                        size="small"
                        aria-label={props.tip}
                        aria-haspopup="dialog"
                        aria-expanded={props.expanded}
                    >
                        <Icon type={props.iconType} size="sm" />
                    </IconButton>
                </Tooltip>
            );
        };
        const toggle = (a, b) => {
            setActivateHelpModals(a);
            setActivateDateModals(b);
        };
        const disabled = !section;
        return (
            <div className="input-group-append d-flex">
                <InputbarButton
                    className="info"
                    iconType="information"
                    tip="Commands cheat-sheet"
                    disabled={disabled || activateHelpModals}
                    expanded={activateHelpModals}
                    onClick={() => {
                        toggle(!activateHelpModals, false);
                    }}
                />
                {!preferencesContext.hideDateTimePickerFilter && (
                    <InputbarButton
                        className="calendar"
                        iconType="calendar-full"
                        tip="Date-time picker"
                        disabled={disabled || activateDateModals}
                        expanded={activateDateModals}
                        onClick={() => {
                            toggle(false, !activateDateModals);
                        }}
                    />
                )}
            </div>
        );
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const filterValue = preferencesContext.filterBarString;
    // console.log({ filterValue });

    useEffect(() => {
        if (isClientSide) {
            _prepareKeyMap();
            _buildFilterFunction(filterValue);
        }
    }, [keyMap]);

    useEffect(() => {
        if (isClientSide) {
            _buildFilterFunction(filterValue);
        }
    }, [filterValue]);

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    const filterBarClassname = `${SETTINGS.NEWUI ? 'is' : 'not'}-in-table`;
    return (
        <div
            id="filter-bar"
            data-testid="filter-bar"
            className={filterBarClassname}
        >
            <span id="search-bar-wrapper">
                <InputGroup>
                    <div className="input-group-prepend d-flex">
                        <InputGroup.Text>
                            <Icon type="magnifier" className="me-2" />
                        </InputGroup.Text>
                    </div>
                    <InputBar
                        section={section}
                        sectionOptions={sectionOptions}
                        completions={completions}
                        getEditorApi={api => {
                            editorViewRef.current = api.editorView;
                            getEditorApi?.(api);
                        }}
                        activateHelpModals={activateHelpModals}
                        setActivateHelpModals={setActivateHelpModals}
                        activateDateModals={activateDateModals}
                        setActivateDateModals={setActivateDateModals}
                        filterValue={filterValue}
                        setFilterValue={preferencesContext.setFilterBarString}
                        handleSection={handleSection}
                        handleChange={value => {
                            //preferencesContext.setFilterBarString(value);
                            if (!value.length) {
                                // note: it trigger InputBar handleChange that already store preferences
                                preferencesContext.clearFilterBar({
                                    savePreferences: true
                                });
                            }
                        }}
                        handleKeyPress={event => {
                            // Only process on Enter key to avoid unnecessary string conversions
                            if (event.key !== 'Enter') {
                                return;
                            }

                            // Get value from editor state, fallback to filterValue only if editor not initialized
                            const value = (
                                editorViewRef.current?.state.doc.toString() ??
                                filterValue ??
                                ''
                            ).trim();

                            if (value) {
                                searchValue(value);
                            }
                        }}
                    />
                    {(enableQueries ?? true) &&
                    (preferencesContext.useQueries ?? true) ? (
                        <Query
                            filterValue={filterValue}
                            clearFilter={preferencesContext.clearFilterBar}
                            enableBookmarks={enableBookmarks}
                            updateQueries={updateQueries}
                            availableQueries={availableQueries}
                            hideDateTimePickerFilter={
                                preferencesContext.hideDateTimePickerFilter
                            }
                        >
                            {renderInfoAndCalendar()}
                        </Query>
                    ) : (
                        <>
                            {!!filterValue && (
                                <div className="input-group-append d-flex">
                                    <IconButton
                                        onClick={preferencesContext.clearFilterBar(
                                            {
                                                savePreferences: true
                                            }
                                        )}
                                        size="small"
                                    >
                                        <Icon type="cross" size="sm" />
                                    </IconButton>
                                </div>
                            )}
                            {renderInfoAndCalendar()}
                        </>
                    )}
                </InputGroup>
            </span>
        </div>
    );
};

FilterBar.propTypes = {
    /**
     * @note keyMap:
     * An object to map internal key and user-friendly label:
     * keys are labels, while the corresponding value are the original keys to looking for in items.
     */
    keyMap: PropTypes.object,
    dropdown: PropTypes.array,
    availableQueries: PropTypes.array,
    updateQueries: PropTypes.func,
    enableBookmarks: PropTypes.bool,
    enableQueries: PropTypes.bool,
    section: PropTypes.string
};

FilterBar.defaultProps = {
    availableQueries: [],
    keyMap: {}
};

export default FilterBar;
